/**
 * useRecordingHandlers — iter-245
 *
 * Bundles the two main recording-button handlers that used to live
 * inline inside `ELPMStudioDAW.jsx`:
 *
 *   • `handleRecord` (the transport's main red dot) — handles all four
 *     of: stop-multi, stop-single, start-multi-track, start-single.
 *     Includes the iter-203 P0 fix (STOP-paths run before START-paths
 *     so a single-track recording can never be silently orphaned by
 *     a still-armed channel set), the iter-223 mic-alias name
 *     resolver, and the iter-203 auto-disarm pass + skipped-dup +
 *     auto-trim toasts on stop.
 *
 *   • `handleRecordChannel` (the per-row red dot on each MixerRow) —
 *     "click a single row → record into it directly" without going
 *     through the arm flow.
 *
 * Extracting them shrinks the parent file by ~290 lines and gives the
 * recording logic a stable, testable surface. Behaviour is intentionally
 * byte-identical to the inline versions — this is a pure cut/paste with
 * NO logic changes.
 *
 * All inputs go through a single options bag so the call site stays
 * readable. Engine ops are async; handlers return Promises.
 */
import { toast } from "sonner";
import { padCh, TOTAL_CHANNELS } from "@/components/daw/dawHelpers";
import { showMicHelp, startRecordingWithTimeout } from "@/lib/micHelp";

export default function useRecordingHandlers({
  engine,
  tracking,
  setTracking,
  recordingIdx,
  setRecordingIdx,
  setMaxDur,
  setLoadedCount,
  armedChannels,
  setArmedChannels,
  setStreamSessionId,
  setStreamBytesUploaded,
  streamToDisk,
  inputDeviceId,
  sourceMap,
  selectedChannel,
  setSelectedChannel,
  checkMicAvailability,
  mapMicError,
  toastErrorWithHelp,
  // iter-246 — optional callback to push the PREVIOUS buffer of a
  // channel into per-channel take history before the new take
  // overwrites it. Called once per successful capture.
  pushTakeToHistory,
}) {
  const handleRecord = async () => {
    // iter-203 P0 FIX — STOP-paths ALWAYS run first before any START-path.
    // Pre-iter-203 bug: if `recordingIdx` was set (single-track active) AND
    // the user had armed channels (multi-track config still set), the
    // `armedChannels.size > 0 || tracking` branch fired and tried to START
    // a tracking session — silently orphaning the single-track recording.
    // Visual symptom: Stop button "does nothing". Always handle STOP first.
    //
    // ALSO defensive: use engine.isTracking() as the source of truth, in
    // case React `tracking` state lags behind engine reality.
    const engineTracking = engine.isTracking?.() === true;

    // ── STOP multi-track tracking ─────────────────────────────────────
    if (tracking || engineTracking) {
      // iter-246 — snapshot the PREVIOUS buffer of every armed channel
      // BEFORE stopTrackingSession overwrites it, so we can push the
      // old takes into per-channel history once the new takes land.
      const previousByCh = {};
      if (pushTakeToHistory) {
        for (const ch of armedChannels) {
          const prev = engine.channels?.[ch]?.buffer;
          if (prev) previousByCh[ch] = { buffer: prev, name: engine.channels[ch]?.name };
        }
      }
      const res = await engine.stopTrackingSession();
      setTracking(false);
      setStreamSessionId(null);
      if (res?.ok) {
        const okCount = res.results.filter((r) => r.ok).length;
        setMaxDur(engine.maxDuration);
        setLoadedCount(engine.channels.filter((c) => c.buffer).length);
        // iter-246 — push the previous take of each successfully-recorded
        // channel into history (skip channels that had no prior buffer).
        if (pushTakeToHistory) {
          for (const r of res.results) {
            if (!r.ok) continue;
            const prev = previousByCh[r.ch];
            if (prev && prev.buffer && prev.buffer !== engine.channels?.[r.ch]?.buffer) {
              pushTakeToHistory(r.ch, prev.buffer, prev.name);
            }
          }
        }
        // iter-203 — auto-disarm channels that successfully captured audio so
        // the next take doesn't accidentally bleed the same mic into all the
        // previously armed channels. Users must explicitly re-arm for the
        // next take.
        const okChannels = res.results.filter((r) => r.ok).map((r) => r.ch);
        if (okChannels.length > 0) {
          setArmedChannels((prev) => {
            const next = new Set(prev);
            okChannels.forEach((ch) => next.delete(ch));
            return next;
          });
        }
        const desc = res.streaming
          ? res.results
              .map((r) => `CH${padCh(r.ch)} ${r.ok ? `✓ ${(r.bytes / 1024).toFixed(0)} KB` : `✗ ${r.error}`}${r.loadError ? ` (load: ${r.loadError})` : ""}`)
              .join(" · ")
          : res.results
              .map((r) => `CH${padCh(r.ch)} ${r.ok ? `✓ ${r.duration.toFixed(1)}s${r.trimmedSec > 0.05 ? ` (-${r.trimmedSec.toFixed(1)}s sil)` : ""}` : `✗ ${r.error}`}`)
              .join(" · ");
        toast.success(`Tracked ${okCount} channel${okCount === 1 ? "" : "s"}${res.streaming ? " · streamed to disk" : ""}`, {
          description: desc,
          duration: 10000,
        });
        // iter-203 — surface duplicate-input drops so the user knows WHY
        // certain armed channels didn't get audio loaded. The engine adds
        // `skippedDups: [{ch, dupOf}]` to the return value when multiple
        // armed channels mapped to the same input source.
        if (Array.isArray(res.skippedDups) && res.skippedDups.length > 0) {
          toast.message("Some channels shared the same input — only one recorded", {
            description: res.skippedDups
              .map((d) => `CH ${padCh(d.ch)} shared input with CH ${padCh(d.dupOf)} — skipped`)
              .join(" · "),
            duration: 10000,
          });
        }
        // Find any tracks where auto-trim shaved meaningful silence; offer a
        // bulk undo so a user can revert all in one click.
        const trimmedTracks = res.results.filter((r) => r.ok && r.trimmedSec > 0.05);
        if (trimmedTracks.length > 0) {
          const totalTrimmed = trimmedTracks.reduce((a, r) => a + r.trimmedSec, 0);
          toast.message(`Auto-trimmed ${totalTrimmed.toFixed(1)}s of silence across ${trimmedTracks.length} channel${trimmedTracks.length === 1 ? "" : "s"}`, {
            description: trimmedTracks.map((r) => `CH ${padCh(r.ch)} -${r.trimmedSec.toFixed(1)}s`).join(" · "),
            duration: 9000,
            action: {
              label: "Undo all",
              onClick: () => {
                let restored = 0;
                trimmedTracks.forEach((r) => { if (engine.undoLastTrim(r.ch)) restored++; });
                setMaxDur(engine.maxDuration);
                if (restored > 0) toast.success(`Silence restored on ${restored} channel${restored === 1 ? "" : "s"}`);
              },
            },
          });
        }
      } else {
        toast.error("Tracking stop failed", { description: res?.error });
      }
      return;
    }

    // ── STOP single-channel recording ─────────────────────────────────
    if (recordingIdx !== null) {
      // iter-246 — snapshot the previous buffer BEFORE stopRecording
      // overwrites the channel slot, so we can push it into the take
      // history once the new take lands.
      const prevSingle = pushTakeToHistory
        ? { buffer: engine.channels?.[recordingIdx]?.buffer, name: engine.channels?.[recordingIdx]?.name }
        : null;
      const res = await engine.stopRecording();
      setRecordingIdx(null);
      if (res?.buffer) {
        setMaxDur(engine.maxDuration);
        setLoadedCount((c) => c + 1);
        // iter-246 — push the displaced buffer (if any) into history. We
        // skip the push when there was no prior buffer (first take on a
        // fresh channel) or when the engine reused the same buffer ref
        // (defensive — shouldn't happen but cheap to guard).
        if (pushTakeToHistory && prevSingle?.buffer && prevSingle.buffer !== res.buffer) {
          pushTakeToHistory(res.channel, prevSingle.buffer, prevSingle.name);
        }
        // Auto-select & scroll to the channel that just got the recording so
        // the user can immediately see the waveform without hunting for it.
        setSelectedChannel(res.channel);
        toast.success(`Captured on CH ${padCh(res.channel)}`, {
          description: `Duration ${res.buffer.duration.toFixed(2)}s · loaded into channel ${padCh(res.channel)}`,
          duration: 6000,
        });
        // Auto-trim toast — fires only when trimLeadingSilence actually shaved
        // some seconds off the take. One-click undo restores the original.
        // When mode === 'print', the same undo also restores the dry buffer.
        if (res.printed) {
          toast.message("Take printed through FX rack", {
            description: `CH ${padCh(res.channel)} buffer now baked with the channel's effects. Undo to get the dry take back.`,
            duration: 8000,
            action: {
              label: "Undo print",
              onClick: () => {
                if (engine.undoLastTrim(res.channel)) {
                  setMaxDur(engine.maxDuration);
                  toast.success("Restored dry take");
                }
              },
            },
          });
        } else if (res.trimmedSec > 0.05) {
          toast.message(`Trimmed ${res.trimmedSec.toFixed(1)}s of leading silence`, {
            description: `CH ${padCh(res.channel)} now starts at the first audible note. Click Undo if you wanted to keep the intro.`,
            duration: 8000,
            action: {
              label: "Undo",
              onClick: () => {
                if (engine.undoLastTrim(res.channel)) {
                  setMaxDur(engine.maxDuration);
                  toast.success("Silence restored");
                }
              },
            },
          });
        }
      } else {
        // iter-150 — distinguish "decoder couldn't read" from "mic was silent"
        // since they require different fixes (browser bug vs OS mic config).
        const msg = res?.error?.message || "";
        const isSilent = /silent|peak/i.test(msg);
        toast.error(isSilent ? "Recording was silent" : "Recording failed to decode", {
          description: msg || "The browser delivered an audio blob the decoder couldn't read. Try recording again.",
          duration: 12000,
        });
      }
      return;
    }

    // Wake the audio engine inside the click itself so the first take isn't clipped.
    if (engine.ctx?.state === "suspended") engine.ctx.resume().catch(() => {});

    // ── START multi-track tracking (any channels armed) ───────────────
    if (armedChannels.size > 0) {
      // Pre-flight before opening multi-track stream.
      const pre = checkMicAvailability();
      if (pre) {
        toast.error(pre.title, {
          description: pre.hint,
          duration: 8000,
          action: pre.code === "iframe-blocked" || pre.code === "security-blocked"
            ? { label: "Open in new tab", onClick: () => window.open(window.location.href, "_blank", "noopener") }
            : undefined,
        });
        return;
      }
      // Sync engine arm map with React state.
      engine.clearArmed();
      armedChannels.forEach((ch) => engine.armChannel(ch, sourceMap[ch] ?? 0));
      engine.setInputDevice(inputDeviceId);

      // If stream-to-disk is on, create a backend recording session FIRST,
      // then pass its sessionId into the engine so every MediaRecorder chunk
      // goes straight to /api/recording/chunk/{id}/{ch}.
      let streamConfig = null;
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (streamToDisk) {
        try {
          const channels = Array.from(armedChannels);
          const res = await fetch(`${backendUrl}/api/recording/start`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              channels,
              sample_rate: engine.ctx.sampleRate,
              mime: "audio/webm",
              label: `Session ${new Date().toLocaleString()}`,
            }),
          });
          if (!res.ok) throw new Error(`Backend start failed: ${res.status}`);
          const body = await res.json();
          streamConfig = { backendUrl, sessionId: body.id, timesliceMs: 2000 };
          setStreamSessionId(body.id);
          setStreamBytesUploaded(0);
        } catch (e) {
          toastErrorWithHelp("Could not start backend recording session", {
            description: e.message,
            probe: "backend",
          });
          return;
        }
      } else {
        setStreamSessionId(null);
      }

      try {
        // iter-223 — Build a name-resolver that the engine will invoke
        // when each per-channel recording completes. Returns the
        // user's mic alias + "Take N" where N auto-increments per
        // alias by scanning existing channel names. If the user
        // never set an alias for that input, return null and the
        // engine falls back to its default Track-CHnn-inN.webm
        // naming. Computed once per tracking session so the take
        // counters don't drift if recordings finish out-of-order.
        const aliasMapForRun = {};
        try {
          aliasMapForRun.__source = JSON.parse(localStorage.getItem("elpm:mic-aliases") || "{}") || {};
        } catch (e) { void e; aliasMapForRun.__source = {}; }
        const takeCountByAlias = {};
        const nameResolver = (ch, actualSrc) => {
          const alias = aliasMapForRun.__source?.[String(actualSrc)];
          if (!alias || typeof alias !== "string") return null;
          // Seed each alias' take counter from existing channel names so
          // CH 04 saved as "SM7B Take 3" still increments to 4, not 1.
          if (!(alias in takeCountByAlias)) {
            const re = new RegExp(`^${alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} Take (\\d+)`);
            let max = 0;
            engine.channels.forEach((c) => {
              const m = c?.name && re.exec(c.name);
              if (m) max = Math.max(max, parseInt(m[1], 10) || 0);
            });
            takeCountByAlias[alias] = max;
          }
          takeCountByAlias[alias] += 1;
          return `${alias} Take ${takeCountByAlias[alias]}`;
        };
        const res = await engine.startTrackingSession({ deviceId: inputDeviceId, streamToDisk: streamConfig, nameResolver });
        if (!res.ok) {
          toast.error("Tracking failed to start", { description: res.error });
          return;
        }
        setTracking(true);
        const cap = res.channelCountActual;
        const req = res.channelCountRequested;
        const desc = res.streamingToDisk
          ? `Streaming to backend disk · 2 s chunks · session ${streamConfig.sessionId.slice(0, 8)}…`
          : (cap < req
              ? `Interface gave us ${cap} of ${req} requested input channels — extra channels will mirror input 1.`
              : `Recording ${cap}-channel stream. Click Record again to stop.`);
        toast.success(`Tracking ${res.channels.length} channel${res.channels.length === 1 ? "" : "s"}`, {
          description: desc,
          duration: 6000,
        });
        // iter-203 — surface duplicate-input drops on START so the user knows
        // upfront which armed channels won't actually record (avoids the
        // confusion of seeing only some channels load after Stop).
        if (Array.isArray(res.skippedDups) && res.skippedDups.length > 0) {
          toast.message("Duplicate input mapping — some armed channels skipped", {
            description: res.skippedDups
              .map((d) => `CH ${padCh(d.ch)} shares input with CH ${padCh(d.dupOf)} — won't record`)
              .join(" · "),
            duration: 10000,
          });
        }
      } catch (e) {
        const mapped = mapMicError(e);
        showMicHelp(e, () => handleRecord());
        console.warn("[mic·tracking]", mapped.code, mapped.title, "→", e);
      }
      return;
    }

    // ── START single-channel recording on the selected channel ────────
    const pre = checkMicAvailability();
    if (pre) {
      toast.error(pre.title, {
        description: pre.hint,
        duration: 8000,
        action: pre.code === "iframe-blocked" || pre.code === "security-blocked"
          ? { label: "Open in new tab", onClick: () => window.open(window.location.href, "_blank", "noopener") }
          : undefined,
      });
      return;
    }
    try {
      await startRecordingWithTimeout(engine, selectedChannel, { deviceId: inputDeviceId });
      setRecordingIdx(selectedChannel);
      toast(`Recording to CH ${padCh(selectedChannel)}`, { description: "Click record again to stop." });
    } catch (e) {
      const mapped = mapMicError(e);
      showMicHelp(e, () => handleRecord());
      console.warn("[mic]", mapped.code, mapped.title, "→", e);
    }
  };

  /* ─── One-click per-row record: select track → start recording on it.
   *  Bypasses the arm flow entirely so a user who just wants to lay down
   *  a quick take on a single channel doesn't have to arm-first then hit
   *  transport-record. Also handles the "I clicked record on a different
   *  channel" case by stopping the existing take before starting the new one.
   *  When the user is in the middle of a multi-track tracking session, we
   *  block this with a clear toast — they should use the transport Stop. */
  const handleRecordChannel = async (channelIdx) => {
    if (channelIdx < 0 || channelIdx >= TOTAL_CHANNELS) return;
    if (tracking) {
      toast.error("Multi-track tracking is active", {
        description: "Click Stop in the transport bar (or Tracking Controls) to end the session first.",
      });
      return;
    }
    // Same channel already recording → stop + decode (toggle behavior).
    if (recordingIdx === channelIdx) {
      await handleRecord();
      return;
    }
    // Different channel currently recording → stop that, then start on the new one.
    if (recordingIdx !== null) {
      await handleRecord();
    }
    // Clear any leftover arm state — per-row record is explicitly single-track.
    if (armedChannels.size > 0) {
      setArmedChannels(new Set());
      engine.clearArmed();
    }
    setSelectedChannel(channelIdx);
    const pre = checkMicAvailability();
    if (pre) {
      toast.error(pre.title, {
        description: pre.hint,
        duration: 8000,
        action: pre.code === "iframe-blocked" || pre.code === "security-blocked"
          ? { label: "Open in new tab", onClick: () => window.open(window.location.href, "_blank", "noopener") }
          : undefined,
      });
      return;
    }
    try {
      await startRecordingWithTimeout(engine, channelIdx, { deviceId: inputDeviceId });
      setRecordingIdx(channelIdx);
      toast(`Recording to CH ${padCh(channelIdx)}`, {
        description: "Click the red dot again to stop.",
      });
    } catch (e) {
      const mapped = mapMicError(e);
      showMicHelp(e, () => handleRecordChannel(channelIdx));
      console.warn("[mic·per-row]", mapped.code, mapped.title, "→", e);
    }
  };

  return { handleRecord, handleRecordChannel };
}
